import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import {
  Search,
  X,
  CreditCard,
  FileText,
  CheckSquare,
  Activity,
  Calendar,
  Settings,
  ArrowRight,
  TrendingDown,
  TrendingUp,
  Target,
  Bookmark,
  Globe,
} from 'lucide-react';
import { formatMoney } from '../../lib/i18n';

export const GlobalSearchModal: React.FC = () => {
  const {
    isSearchOpen,
    setIsSearchOpen,
    transactions,
    notes,
    tasks,
    habits,
    yearlyGoals,
    resources,
    currency,
    language,
    setCurrentPage,
  } = useApp();

  const [query, setQuery] = useState('');

  // Keyboard shortcut listener (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
      if (e.key === 'Escape' && isSearchOpen) {
        setIsSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen, setIsSearchOpen]);

  if (!isSearchOpen) return null;

  const cleanQuery = query.toLowerCase().trim();

  const matchedTransactions = cleanQuery
    ? transactions.filter(
        (t) =>
          t.note?.toLowerCase().includes(cleanQuery) ||
          t.categoryName?.toLowerCase().includes(cleanQuery) ||
          t.amount.toString().includes(cleanQuery)
      )
    : [];

  const matchedNotes = cleanQuery
    ? notes.filter(
        (n) =>
          !n.isTrashed &&
          (n.title.toLowerCase().includes(cleanQuery) ||
            (!n.isLocked && n.content?.toLowerCase().includes(cleanQuery)))
      )
    : [];

  const matchedTasks = cleanQuery
    ? tasks.filter(
        (t) =>
          t.title.toLowerCase().includes(cleanQuery) ||
          t.description?.toLowerCase().includes(cleanQuery)
      )
    : [];

  const matchedHabits = cleanQuery
    ? habits.filter((h) => h.name.toLowerCase().includes(cleanQuery))
    : [];

  const matchedGoals = cleanQuery
    ? (yearlyGoals || []).filter(
        (g) =>
          g.title.toLowerCase().includes(cleanQuery) ||
          g.description?.toLowerCase().includes(cleanQuery) ||
          g.resources?.toLowerCase().includes(cleanQuery)
      )
    : [];

  const matchedResources = cleanQuery
    ? (resources || []).filter(
        (r) =>
          r.title.toLowerCase().includes(cleanQuery) ||
          r.url.toLowerCase().includes(cleanQuery) ||
          r.description?.toLowerCase().includes(cleanQuery) ||
          (r.tags && r.tags.some((tag) => tag.toLowerCase().includes(cleanQuery)))
      )
    : [];

  const totalMatches =
    matchedTransactions.length +
    matchedNotes.length +
    matchedTasks.length +
    matchedHabits.length +
    matchedGoals.length +
    matchedResources.length;

  const navigateTo = (page: any) => {
    setCurrentPage(page);
    setIsSearchOpen(false);
    setQuery('');
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) setIsSearchOpen(false);
      }}
      className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-black/60 backdrop-blur-sm animate-fade-in"
    >
      <div
        className="w-full max-w-2xl bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center px-4 py-3.5 border-b border-slate-100 dark:border-slate-800 gap-3">
          <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search resources, transactions, notes, tasks, habits, or navigation..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            className="w-full bg-transparent text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none text-base"
          />
          <button
            onClick={() => setIsSearchOpen(false)}
            className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Results Container */}
        <div className="max-h-[60vh] overflow-y-auto p-4 space-y-4">
          {/* Quick Navigation suggestions */}
          {!cleanQuery && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 px-2">
                Quick Navigation
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {[
                  { label: 'Dashboard', page: 'dashboard', icon: Calendar },
                  { label: 'Finance & Wallets', page: 'finance', icon: CreditCard },
                  { label: 'Smart Notes', page: 'notes', icon: FileText },
                  { label: 'Daily Tasks', page: 'tasks', icon: CheckSquare },
                  { label: 'Year Goals', page: 'goals', icon: Target },
                  { label: 'Resources & Links', page: 'resources', icon: Bookmark },
                  { label: 'Habit Tracker', page: 'habits', icon: Activity },
                  { label: 'Reports & Export', page: 'reports', icon: TrendingUp },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.page}
                      onClick={() => navigateTo(item.page)}
                      className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 hover:bg-brand-500/10 dark:hover:bg-brand-500/10 text-slate-700 dark:text-slate-200 hover:text-brand-600 dark:hover:text-brand-400 transition-colors text-xs font-medium text-left"
                    >
                      <Icon className="w-4 h-4" />
                      <span className="truncate">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {cleanQuery && totalMatches === 0 && (
            <div className="py-12 text-center text-slate-400">
              <p className="text-sm">No results found for &ldquo;{query}&rdquo;</p>
              <p className="text-xs text-slate-500 mt-1">Try searching for amounts, tags, titles or notes</p>
            </div>
          )}

          {/* Transactions */}
          {matchedTransactions.length > 0 && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 px-2">
                Transactions ({matchedTransactions.length})
              </span>
              <div className="space-y-1.5 mt-2">
                {matchedTransactions.slice(0, 5).map((t) => (
                  <div
                    key={t.id}
                    onClick={() => navigateTo('finance')}
                    className="flex items-center justify-between p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/80 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                          t.type === 'income'
                            ? 'bg-emerald-500/10 text-emerald-500'
                            : 'bg-rose-500/10 text-rose-500'
                        }`}
                      >
                        {t.type === 'income' ? (
                          <TrendingUp className="w-4 h-4" />
                        ) : (
                          <TrendingDown className="w-4 h-4" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-900 dark:text-white">
                          {t.note || t.categoryName}
                        </div>
                        <div className="text-xs text-slate-500">{t.date} • {t.accountName}</div>
                      </div>
                    </div>
                    <span
                      className={`text-sm font-semibold ${
                        t.type === 'income' ? 'text-emerald-500' : 'text-rose-500'
                      }`}
                    >
                      {t.type === 'income' ? '+' : '-'}
                      {formatMoney(t.amount, currency, language)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {matchedNotes.length > 0 && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 px-2">
                Notes ({matchedNotes.length})
              </span>
              <div className="space-y-1.5 mt-2">
                {matchedNotes.slice(0, 4).map((n) => (
                  <div
                    key={n.id}
                    onClick={() => navigateTo('notes')}
                    className="p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/80 cursor-pointer transition-colors"
                  >
                    <div className="text-sm font-medium text-slate-900 dark:text-white flex items-center gap-2">
                      <FileText className="w-4 h-4 text-brand-500" />
                      {n.title}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-1 mt-1">{n.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tasks */}
          {matchedTasks.length > 0 && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 px-2">
                Tasks ({matchedTasks.length})
              </span>
              <div className="space-y-1.5 mt-2">
                {matchedTasks.slice(0, 4).map((tk) => (
                  <div
                    key={tk.id}
                    onClick={() => navigateTo('tasks')}
                    className="flex items-center justify-between p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/80 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-2.5">
                      <CheckSquare className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium text-slate-900 dark:text-white">
                        {tk.title}
                      </span>
                    </div>
                    <span className="text-xs text-slate-400">{tk.priority} priority</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Goals & Plans */}
          {matchedGoals.length > 0 && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 px-2">
                Yearly Goals & Learning ({matchedGoals.length})
              </span>
              <div className="space-y-1.5 mt-2">
                {matchedGoals.slice(0, 4).map((g) => (
                  <div
                    key={g.id}
                    onClick={() => navigateTo('goals')}
                    className="flex items-center justify-between p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/80 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-2.5">
                      <Target className="w-4 h-4 text-emerald-500" />
                      <div>
                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                          {g.title}
                        </span>
                        <div className="text-xs text-slate-400">
                          {g.category} • {g.progress}% completed
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                      {g.quarter || 'Year'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resources & Links */}
          {matchedResources.length > 0 && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 px-2">
                Resources & Links ({matchedResources.length})
              </span>
              <div className="space-y-1.5 mt-2">
                {matchedResources.slice(0, 5).map((res) => (
                  <div
                    key={res.id}
                    onClick={() => navigateTo('resources')}
                    className="flex items-center justify-between p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/80 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Bookmark className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-slate-900 dark:text-white truncate block">
                          {res.title}
                        </span>
                        <div className="text-xs font-mono text-slate-400 truncate">
                          {res.url}
                        </div>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 flex-shrink-0">
                      {res.category}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer shortcuts */}
        <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs text-slate-400">
          <span>Press ESC to close</span>
          <span className="flex items-center gap-1">
            Navigate with clicks or shortcuts <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </div>
  );
};
